import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Music, Play, TrendingUp, ArrowUpRight, Calendar, BarChart3, Download, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { useLegalGate } from '../components/LegalAcceptanceModal';
import ArtistFirstWinPanel from '../components/onboarding/ArtistFirstWinPanel';
import { toast } from 'sonner';
import { API_BASE as API } from '../lib/apiBase';


const CorcheaIcon = ({ size = 14, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 3a1 1 0 011 1v10.27A4 4 0 1111 18.13V4a1 1 0 011-1zm0 17a2 2 0 100-4 2 2 0 000 4z"/>
    <path d="M13 4h5v3h-5V4z"/>
  </svg>
);

const fmt = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n?.toLocaleString() || '0';
};

export default function ArtistDashboardPage() {
  const { token } = useAuth();
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [requestingLiq, setRequestingLiq] = useState(false);
  const legalLiq = useLegalGate('request_liquidation');

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    Promise.all([
      fetch(`${API}/api/artist/dashboard`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API}/api/artist/billing`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    ]).then(([d, b]) => {
      setData(d);
      setBilling(b);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen pt-14 flex items-center justify-center"><div className="w-10 h-10 border-2 border-pulse-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!data || data.detail) return (
    <div className="min-h-screen pt-14 flex flex-col items-center justify-center gap-3 text-pulse-muted">
      <Music size={40} className="text-pulse-primary" />
      <p>{lang === 'es' ? 'Necesitas un perfil de artista para acceder' : 'You need an artist profile to access this'}</p>
      <Link to="/profile" className="text-pulse-primary text-sm underline">{lang === 'es' ? 'Ir a perfil' : 'Go to profile'}</Link>
    </div>
  );

  const { artist, wallet, songs, totals, this_month: month, recent_supports: supports, commission_rate } = data;

  const handleRequestLiquidation = async () => {
    if (!legalLiq.gate()) return;
    setRequestingLiq(true);
    try {
      const res = await fetch(`${API}/api/fiscal/liquidation-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (res.ok) {
        toast.success(d.message || (lang === 'es' ? 'Solicitud creada' : 'Request created'));
      } else {
        toast.error(d.detail || 'Error');
      }
    } catch { toast.error('Error de conexion'); }
    setRequestingLiq(false);
  };

  return (
    <div data-testid="artist-dashboard" className="min-h-screen pt-14 pb-32">
      {legalLiq.legalModal}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Link to="/profile" className="flex items-center gap-2 text-pulse-muted hover:text-white mb-6 transition-colors"><ArrowLeft size={18} /> {lang === 'es' ? 'Volver' : 'Back'}</Link>

        <ArtistFirstWinPanel
          lang={lang}
          songs={songs}
          artistPublicId={artist?.id}
          className="mb-8"
          density="full"
        />

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          {artist.photo && <img src={artist.photo} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-amber-500/30" />}
          <div className="min-w-0 flex-1">
            <h1 data-testid="artist-name" className="text-2xl font-heading font-extrabold">{artist.name}</h1>
            <p className="text-sm text-pulse-muted">{lang === 'es' ? 'Panel de Artista' : 'Artist Dashboard'}</p>
            <Link
              to="/artist-booking"
              data-testid="artist-dashboard-link-booking"
              className="text-xs text-pulse-primary font-semibold mt-2 inline-block hover:underline"
            >
              {lang === 'es' ? 'Perfil de contratación (sistema) →' : 'Booking system profile →'}
            </Link>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-pulse-border pb-2">
          {['overview', 'songs', 'billing'].map(t => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-t-lg text-sm font-semibold transition-all ${tab === t ? 'bg-pulse-surface text-pulse-primary border-b-2 border-pulse-primary' : 'text-pulse-muted hover:text-white'}`}>
              {t === 'overview' && (lang === 'es' ? 'Resumen' : 'Overview')}
              {t === 'songs' && (lang === 'es' ? 'Canciones' : 'Songs')}
              {t === 'billing' && (lang === 'es' ? 'Liquidaciones' : 'Settlements')}
            </button>
          ))}
        </div>

        {/* Overview */}
        {tab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard icon={<CorcheaIcon size={18} className="text-amber-400" />} label={lang === 'es' ? 'Balance' : 'Balance'} value={fmt(wallet.balance)} sub={`${wallet.balance_fiat.toFixed(2)}EUR`} color="amber" />
              <StatCard icon={<Play size={18} className="text-cyan-400" />} label="Plays" value={fmt(artist.plays)} sub={`${songs.length} tracks`} color="cyan" />
              <StatCard icon={<TrendingUp size={18} className="text-green-400" />} label={lang === 'es' ? 'Ingresos Mes' : 'Month Income'} value={fmt(month.income)} sub={`${month.income_fiat}EUR`} color="green" />
              <StatCard icon={<ArrowUpRight size={18} className="text-orange-400" />} label={lang === 'es' ? 'Est. Mes' : 'Month Est.'} value={fmt(month.net)} sub={lang === 'es' ? 'estimacion corcheas' : 'estimated corcheas'} color="orange" />
            </div>

            {/* All-time Totals */}
            <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
              <h3 className="text-sm font-heading font-bold mb-3 flex items-center gap-2"><BarChart3 size={16} className="text-pulse-primary" /> {lang === 'es' ? 'Resumen Total' : 'All-Time Summary'}</h3>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Ingresos totales' : 'Total income'}</p><p className="text-lg font-bold text-green-400">{fmt(totals.total_income)}</p></div>
                <div><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Gastos' : 'Expenses'}</p><p className="text-lg font-bold text-red-400">{fmt(totals.total_expenses)}</p></div>
                <div><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Estimacion' : 'Estimate'}</p><p className="text-lg font-bold text-white">{fmt(totals.net)}</p></div>
              </div>
              <p className="text-[10px] text-pulse-muted mt-3 text-center">{lang === 'es' ? `Comision plataforma: ${(commission_rate * 100).toFixed(0)}%` : `Platform fee: ${(commission_rate * 100).toFixed(0)}%`}</p>
            </div>

            {/* Recent Supports */}
            {supports.length > 0 && (
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
                <h3 className="text-sm font-heading font-bold mb-3 flex items-center gap-2"><CorcheaIcon size={16} className="text-amber-400" /> {lang === 'es' ? 'Apoyos Recientes' : 'Recent Supports'}</h3>
                <div className="space-y-2">
                  {supports.map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-sm p-2 rounded-lg bg-pulse-elevated">
                      <span className="text-pulse-muted">{s.from_user_name}</span>
                      <div className="text-right">
                        <span className="text-amber-400 font-semibold">{s.amount_corcheas || s.net_corcheas || 0} corcheas</span>
                        {s.message && <p className="text-[10px] text-pulse-muted italic max-w-[200px] truncate">"{s.message}"</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Songs Tab */}
        {tab === 'songs' && (
          <div className="space-y-2">
            {songs.length === 0 ? (
              <p className="text-center text-pulse-muted py-8">{lang === 'es' ? 'Sin canciones publicadas' : 'No songs published'}</p>
            ) : (
              songs.sort((a, b) => (b.plays || 0) - (a.plays || 0)).map(song => (
                <div key={song.id} data-testid={`dashboard-song-${song.id}`} className="flex items-center justify-between p-3 bg-pulse-surface border border-pulse-border rounded-xl">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{song.title_es || song.title || 'Sin titulo'}</p>
                    <p className="text-[10px] text-pulse-muted">{new Date(song.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1"><Play size={12} className="text-cyan-400" /> {fmt(song.plays || 0)}</span>
                    <span className="flex items-center gap-1"><CorcheaIcon size={12} className="text-amber-400" /> {fmt(song.corcheas_received || 0)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Billing Tab */}
        {tab === 'billing' && (
          <div className="space-y-4">
            {/* Request Liquidation */}
            <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-heading font-bold flex items-center gap-2">
                  <Download size={16} className="text-green-400" /> {lang === 'es' ? 'Solicitar Liquidacion' : 'Request Settlement'}
                </h3>
                <button
                  onClick={handleRequestLiquidation}
                  disabled={requestingLiq}
                  className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-xl text-sm font-semibold transition disabled:opacity-50"
                  data-testid="request-liquidation-btn"
                >
                  {requestingLiq ? '...' : (lang === 'es' ? 'Solicitar liquidacion' : 'Request settlement')}
                </button>
              </div>
              <div className="flex items-start gap-2 p-2 bg-amber-500/10 rounded-lg">
                <AlertTriangle size={14} className="text-amber-400 mt-0.5 shrink-0" />
                <p className="text-[10px] text-amber-300/80">
                  {lang === 'es'
                    ? 'ESTIMACION ORIENTATIVA. Los importes mostrados son una estimacion. Las liquidaciones reales dependen de tu situacion fiscal. Consulta a tu asesor. PulseMusic emite informes de liquidacion, no facturas fiscales. Minimo 500 corcheas (~5 EUR).'
                    : 'ESTIMATE ONLY. Actual amounts depend on your tax situation. Consult your advisor. Minimum 500 corcheas (~5 EUR).'}
                </p>
              </div>
            </div>

            <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
              <h3 className="text-sm font-heading font-bold mb-2 flex items-center gap-2"><Calendar size={16} className="text-pulse-primary" /> {lang === 'es' ? 'Ciclos de Liquidacion' : 'Settlement Cycles'}</h3>
              {billing?.billing_cycles?.length > 0 ? (
                <div className="space-y-2">
                  {billing.billing_cycles.map((c, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-pulse-elevated text-sm">
                      <div>
                        <p className="font-semibold">{new Date(c.period_start).toLocaleDateString()} - {new Date(c.period_end).toLocaleDateString()}</p>
                        <p className="text-[10px] text-pulse-muted">{c.status}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 font-bold">{fmt(c.artist_payout_corcheas)} corcheas</p>
                        <p className="text-[10px] text-pulse-muted">{c.artist_payout_fiat}EUR</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-pulse-muted py-4 text-center">{lang === 'es' ? 'Sin ciclos cerrados aun. La liquidacion se cierra mensualmente.' : 'No closed cycles yet. Settlements close monthly.'}</p>
              )}
            </div>

            {billing?.payouts?.length > 0 && (
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
                <h3 className="text-sm font-heading font-bold mb-2 flex items-center gap-2"><Download size={16} className="text-green-400" /> {lang === 'es' ? 'Pagos Realizados' : 'Payouts'}</h3>
                {billing.payouts.map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-sm p-2 rounded-lg bg-pulse-elevated mb-1">
                    <span>{new Date(p.created_at).toLocaleDateString()}</span>
                    <span className="text-green-400 font-bold">{p.amount_fiat || p.amount}EUR</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, sub, color }) {
  const borderColors = { amber: 'border-amber-500/20', cyan: 'border-cyan-500/20', green: 'border-green-500/20', orange: 'border-orange-500/20' };
  return (
    <div className={`bg-pulse-surface border ${borderColors[color] || 'border-pulse-border'} rounded-xl p-4`}>
      <div className="flex items-center gap-2 mb-1">{icon}<span className="text-[10px] text-pulse-muted uppercase tracking-wider">{label}</span></div>
      <p className="text-2xl font-heading font-extrabold">{value}</p>
      <p className="text-[10px] text-pulse-muted">{sub}</p>
    </div>
  );
}
